import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2 } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildCanonical, buildPageMeta } from "@/lib/seoIndex";

const initial = {
  title: "", description: "", organization: "",
  category: "volunteering", cause_area: "",
  country: "", city: "", url: "", contact_email: "",
  image_url: "", impact_metric: "",
};

export default function ListOfferPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const nav = useNavigate();
  const [meta, setMeta] = useState({ categories: [], cause_areas: [], countries: [] });
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const listMeta = buildPageMeta("listOffer");
  useSEO({
    robots: decideRobots({ pageType: "listOffer" }),
    title: listMeta.title,
    description: listMeta.description,
    canonical: buildCanonical("/list-offer"),
  });

  useEffect(() => { api.get("/meta").then(r => setMeta(r.data)); }, []);

  useEffect(() => {
    if (user === false) nav("/login?redirect=/list-offer", { replace: true });
  }, [user, nav]);

  const setField = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach(k => { if (payload[k] === "") delete payload[k]; });
      await api.post("/offers", payload);
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-2xl mx-auto py-20 px-6 text-center" data-testid="list-success">
        <CheckCircle2 size={60} className="mx-auto text-[#5B8C66]" strokeWidth={1.4}/>
        <h1 className="mt-6 text-3xl font-black tracking-tight">{t("list_offer.success_title")}</h1>
        <p className="mt-3 text-[#5F6B61]">{t("list_offer.success_body")}</p>
        <div className="mt-8 flex gap-3 justify-center">
          <button onClick={() => { setSuccess(false); setForm(initial); }} className="btn-pill px-5 py-2.5 border border-[#DED7C7] hover:border-[#5B8C66] font-semibold">{t("list_offer.submit_another")}</button>
          <button onClick={() => nav("/offers")} className="btn-pill px-5 py-2.5 bg-[#5B8C66] text-white font-semibold hover:bg-[#3F6147]">{t("list_offer.browse_offers")}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-8 py-12" data-testid="list-offer-page">
      <p className="eyebrow">{t("list_offer.eyebrow")}</p>
      <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">{t("list_offer.title")}</h1>
      <p className="mt-3 text-[#5F6B61] max-w-xl">{t("list_offer.subtitle")}</p>

      <form onSubmit={submit} className="mt-10 card-clay p-6 sm:p-10 space-y-5" data-testid="list-offer-form">
        <Field label={t("list_offer.f_title")}>
          <Input value={form.title} onChange={(v) => setField("title", v)} required testId="lo-title"/>
        </Field>
        <Field label={t("list_offer.f_organization")}>
          <Input value={form.organization} onChange={(v) => setField("organization", v)} required testId="lo-organization"/>
        </Field>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label={t("list_offer.f_category")}>
            <Select value={form.category} onChange={(v) => setField("category", v)} testId="lo-category">
              {meta.categories.map(c => <option key={c.value} value={c.value}>{t(`categories.${c.value}`, { defaultValue: c.label })}</option>)}
            </Select>
          </Field>
          <Field label={t("list_offer.f_cause")}>
            <Select value={form.cause_area} onChange={(v) => setField("cause_area", v)} required testId="lo-cause">
              <option value="">{t("list_offer.f_cause_select")}</option>
              {meta.cause_areas.map(c => <option key={c} value={c}>{t(`causes.${c}`, { defaultValue: c })}</option>)}
            </Select>
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label={t("list_offer.f_country")}>
            <Select value={form.country} onChange={(v) => setField("country", v)} required testId="lo-country">
              <option value="">{t("list_offer.f_country_select")}</option>
              {meta.countries.map(c => <option key={c} value={c}>{t(`countries.${c}`, { defaultValue: c })}</option>)}
            </Select>
          </Field>
          <Field label={t("list_offer.f_city")}>
            <Input value={form.city} onChange={(v) => setField("city", v)} testId="lo-city"/>
          </Field>
        </div>

        <Field label={t("list_offer.f_description")}>
          <textarea
            value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            required
            rows={5}
            className="w-full px-4 py-3 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white"
            data-testid="lo-description"
          />
        </Field>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label={t("list_offer.f_url")}>
            <Input value={form.url} onChange={(v) => setField("url", v)} testId="lo-url" placeholder="https://"/>
          </Field>
          <Field label={t("list_offer.f_email")}>
            <Input type="email" value={form.contact_email} onChange={(v) => setField("contact_email", v)} testId="lo-email"/>
          </Field>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          <Field label={t("list_offer.f_image")}>
            <Input value={form.image_url} onChange={(v) => setField("image_url", v)} testId="lo-image"/>
          </Field>
          <Field label={t("list_offer.f_impact")}>
            <Input value={form.impact_metric} onChange={(v) => setField("impact_metric", v)} testId="lo-impact" placeholder={t("list_offer.f_impact_placeholder")}/>
          </Field>
        </div>

        {error && <div className="text-sm text-[#3F6147] bg-[#E9F0E0] border border-[#5B8C66]/20 rounded-xl px-4 py-3" data-testid="list-form-error">{error}</div>}

        <div className="pt-2 flex justify-end">
          <button type="submit" disabled={loading} className="btn-pill px-7 py-3 bg-[#5B8C66] text-white font-bold hover:bg-[#3F6147] transition disabled:opacity-60" data-testid="lo-submit">
            {loading ? t("list_offer.submitting") : t("list_offer.submit")}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider text-[#3F6147] mb-2 block">{label}</label>
      {children}
    </div>
  );
}

function Input({ value, onChange, type = "text", required = false, placeholder, testId }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      className="w-full px-4 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white"
      data-testid={testId}
    />
  );
}

function Select({ value, onChange, children, required, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      className="w-full px-4 py-2.5 rounded-xl border border-[#DED7C7] focus:outline-none focus:ring-2 focus:ring-[#5B8C66] text-sm bg-white"
      data-testid={testId}
    >
      {children}
    </select>
  );
}
